import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../utils/Api";
import Preloader from "../Preloader";

export default function SplitStUnitForm({ stUnit, onClose, onSplit }) {
  const [toLocationId, setToLocationId] = useState(stUnit.locationId);
  const [splitQty, setSplitQty] = useState(0);
  const [areas, setAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const api = useApi();

  useEffect(() => {
    async function init() {
      setIsLoading(true);
      const areas = await loadAreas(api);
      setAreas(areas ?? []);
      setIsLoading(false);
    }
    init();
  }, [api]);

  const handleSplitStUnit = useCallback(async () => {
    const request = { stUnitId: stUnit.stUnitId, toLocationId, splitQty };
    const response = await splitStUnit(request, api);
    if (response.isSuccessful) {
      await onSplit();
    }
  }, [api, stUnit, toLocationId, splitQty, onClose, onSplit]);

  return (
    <>
      {isLoading && <Preloader />}
      <DialogTitle>{Translate.get("SplitStUnit")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} paddingTop={1}>
          <Grid size={12}>
            <InnerTextField disabled labelKey="AvaailableStUnitQty" value={stUnit.qty} />
          </Grid>

          <Grid size={12}>
            <InnerTextField
              labelKey="SplitStUnitQty"
              value={splitQty}
              handleUpdateValue={(newValue) => setSplitQty(newValue)}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                type: "number",
                min: 0,
                max: stUnit.qty,
              }}
            />
          </Grid>

          <Grid size={12}>
            <InnerSelectFromList
              required
              labelKey="ToStUnitLocation"
              list={areas}
              itemLabelName="shortName"
              itemValueName="locationId"
              value={toLocationId}
              handleUpdateValue={(newValue) => setToLocationId(newValue)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{Translate.get("Cancel")}</Button>
        <ButtonWithSpinner
          variant="contained"
          onClick={handleSplitStUnit}
          disabled={!toLocationId || !splitQty || splitQty > stUnit.qty}
        >
          {Translate.get("SplitStUnit")}
        </ButtonWithSpinner>
      </DialogActions>
    </>
  );
}

function InnerTextField({
  labelKey,
  inputProps,
  InputProps,
  value,
  handleUpdateValue,
  helperText,
  disabled,
  required,
}) {
  return (
    <TextField
      fullWidth
      required={required}
      disabled={disabled}
      helperText={helperText}
      value={value ?? ""}
      InputProps={{ ...InputProps, spellCheck: false }}
      onChange={(event) => handleUpdateValue(event.target.value)}
      label={Translate.get(labelKey)}
      inputProps={inputProps}
    />
  );
}

function InnerSelect({ labelKey, items, value, handleUpdateValue, disabledFn, hideFn, required }) {
  let sortedItems = items ? [...items].sort((a, b) => a.label.localeCompare(b.label)) : items;
  return (
    <FormControl fullWidth required={required}>
      <InputLabel id={labelKey + "id"}>{Translate.get(labelKey)}</InputLabel>
      <Select
        labelId={labelKey + "id"}
        id={labelKey + "select"}
        value={value ?? ""}
        label={Translate.get(labelKey)}
        onChange={(event) => handleUpdateValue(event.target.value)}
      >
        {sortedItems &&
          sortedItems
            .filter((item) => (hideFn ? !hideFn(item.value) : true))
            .map((item, index) => (
              <MenuItem key={index} value={item.value} disabled={disabledFn ? disabledFn(item.value) : false}>
                {item.label}
              </MenuItem>
            ))}
      </Select>
    </FormControl>
  );
}

function InnerSelectFromList({
  labelKey,
  list,
  itemLabelName,
  itemValueName,
  value,
  handleUpdateValue,
  compareFn,
  required,
}) {
  const items = list.map((item) => ({ label: item[itemLabelName], value: item[itemValueName] }));
  return (
    <InnerSelect
      required={required}
      labelKey={labelKey}
      items={items}
      value={value}
      handleUpdateValue={handleUpdateValue}
      compareFn={compareFn}
    />
  );
}

async function loadAreas(api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/areas`;
  const response = await api.fetch(url, false, "GET");
  if (response.isSuccessful) {
    return response.value;
  } else {
    return null;
  }
}

async function splitStUnit(request, api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/stunits/split`;
  const response = await api.fetch(url, request, "POST");
  return response;
}
